import { SUPPORT_LIMITS } from "@genbaryoku/contract";

/**
 * 問い合わせの「画面の写し」（#124 の 3a の続き、docs/50 §7）。上の帯の「？」を押した瞬間の、見えている範囲を1枚の画像にする。
 *   - 写すのは #root（アプリの画面）。窓（data-nocapture）は写さない。見えている範囲より下の要素は描かない（重くしない）
 *   - パスワード・確認コード・カード番号の欄と data-mask の付いた所は、写しの中で中身を空にし、押した瞬間の位置を塗りつぶす（二重。ほかの文字は伏せない＝伏せると何が問題か分からない）
 *   - React は選んだ選択肢・チェックの状態を属性でなく DOM の値で持つ＝写しには属性で書き直す（チェックは押した瞬間に data-gbcap-checked で控える）
 *   - 描くのは modern-screenshot（MIT。DOM を SVG の foreignObject に写して canvas へ）。「？」を押したときに読む（最初の JS に入れない）
 *   - 字は画面と同じ BIZ UD を写しに入れる（入れないと端末の字で描かれて幅が変わり、折り返し・表の列がずれる。10/4 射場さん「レイアウトが崩れる」）：
 *     画面が読み込んだ @font-face の塊のうち、写す画面の文字を含むものだけを data: にする（Google Fonts の CSS は index.html の
 *     crossorigin で読める・字の本体は fonts.gstatic.com＝CSP の connect-src。ふつうはブラウザの控えから取る）。読めなければ端末の字で描く
 *   - blob: の画像（選んだ写真の見本など）は fetch せず、画面に出ている <img> を canvas に描き直して使う（connect-src に blob: が無い）
 *   - PNG。大きければ JPEG。3MB（SUPPORT_LIMITS.captureBytes）を超えたら写しは付けない
 * 作れないとき（古いブラウザ・時間切れ）は null。問い合わせはそのまま送れる
 */
export type ScreenShot = { blob: Blob; type: "image/png" | "image/jpeg"; url: string; width: number; height: number; /** 保存するときの名前（20261004_2041_見積書画面.png） */ name: string };

/** 塗りつぶす所 */
export const CAPTURE_MASK = 'input[type="password"],input[autocomplete~="one-time-code"],input[autocomplete$="password"],input[autocomplete^="cc-"],[data-mask]';
/** 写さない所（「？」の窓など） */
export const CAPTURE_SKIP = "[data-nocapture]";
const CHECKED = "data-gbcap-checked";

const TIMEOUT_MS = 10_000;
const PNG_MAX = 2 * 1024 * 1024;

type Rect = { x: number; y: number; w: number; h: number };

/** blob: の画像は、画面に出ている <img> を描き直して data: にする（ほかは modern-screenshot の fetch に任せる） */
function blobImages(): (url: string) => Promise<string | false> {
  const loaded = new Map<string, HTMLImageElement>();
  for (const im of Array.from(document.images)) {
    const src = im.currentSrc || im.src;
    if (src.startsWith("blob:") && im.complete && im.naturalWidth > 0) loaded.set(src, im);
  }
  return async (url) => {
    const im = url.startsWith("blob:") ? loaded.get(url) : undefined;
    if (!im) return false;
    try {
      const c = document.createElement("canvas");
      c.width = im.naturalWidth;
      c.height = im.naturalHeight;
      c.getContext("2d")?.drawImage(im, 0, 0);
      return c.toDataURL("image/png");
    } catch {
      return false;
    }
  };
}

const toBlob = (c: HTMLCanvasElement, type: string, q?: number) => new Promise<Blob | null>((res) => c.toBlob(res, type, q));

const FONT_HOST = "https://fonts.gstatic.com/";
const unq = (s: string) => s.trim().replace(/^["']|["']$/g, "");
const faceKey = (family: string, weight: string, style: string, range: string) => [unq(family), weight.trim(), style.trim(), range.replace(/\s+/g, "").toUpperCase()].join("|");

/** unicode-range（U+4E00-4E0F, U+25B3, U+4??）に、cps（小さい順）のどれかが入るか */
export function rangeHits(range: string, cps: number[]): boolean {
  for (const part of range.split(",")) {
    const m = /^\s*U\+([0-9A-F?]+)(?:-([0-9A-F]+))?\s*$/i.exec(part);
    if (!m) continue;
    const lo = parseInt(m[1]!.replace(/\?/g, "0"), 16);
    const hi = m[2] ? parseInt(m[2], 16) : parseInt(m[1]!.replace(/\?/g, "F"), 16);
    // lo 以上の最初の文字を二分探索
    let a = 0;
    let b = cps.length;
    while (a < b) {
      const mid = (a + b) >> 1;
      if (cps[mid]! < lo) a = mid + 1;
      else b = mid;
    }
    if (a < cps.length && cps[a]! <= hi) return true;
  }
  return false;
}

/** 写す画面の文字（本文・欄の値） */
function textOf(root: Element): number[] {
  let t = root.textContent ?? "";
  for (const el of Array.from(root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>("input,textarea"))) t += el.value;
  const set = new Set<number>();
  for (const ch of t) set.add(ch.codePointAt(0)!);
  return [...set].sort((x, y) => x - y);
}

/** 画面が読み込んだ字の塊のうち、写す文字を含むものを data: の @font-face にする。読めなければ null（端末の字で描く） */
async function fontCss(root: Element): Promise<string | null> {
  const loaded = new Set<string>();
  document.fonts.forEach((f) => {
    if (f.status === "loaded") loaded.add(faceKey(f.family, f.weight, f.style, f.unicodeRange));
  });
  if (!loaded.size) return null;
  const cps = textOf(root);
  const want: { family: string; weight: string; style: string; range: string; url: string }[] = [];
  for (const sheet of Array.from(document.styleSheets)) {
    let rules: CSSRuleList;
    try {
      rules = sheet.cssRules;
    } catch {
      continue; // crossorigin の無い外の CSS は読めない
    }
    for (const r of Array.from(rules)) {
      if (!(r instanceof CSSFontFaceRule)) continue;
      const st = r.style;
      const f = { family: unq(st.getPropertyValue("font-family")), weight: st.getPropertyValue("font-weight") || "400", style: st.getPropertyValue("font-style") || "normal", range: st.getPropertyValue("unicode-range") || "U+0-10FFFF" };
      const url = /url\(\s*["']?([^"')]+)["']?\s*\)/.exec(st.getPropertyValue("src"))?.[1];
      if (!url?.startsWith(FONT_HOST) || !loaded.has(faceKey(f.family, f.weight, f.style, f.range)) || !rangeHits(f.range, cps)) continue;
      want.push({ ...f, url });
    }
  }
  if (!want.length) return null;
  const css = await Promise.all(
    want.map(async (f) => {
      try {
        const res = await fetch(f.url, { cache: "force-cache", mode: "cors", credentials: "omit" });
        if (!res.ok) return "";
        const data = await new Promise<string>((ok, ng) => {
          const fr = new FileReader();
          fr.onload = () => (typeof fr.result === "string" ? ok(fr.result.replace(/^data:[^,]*,/, "")) : ng(new Error("read")));
          fr.onerror = () => ng(fr.error ?? new Error("read"));
          void res.blob().then((b) => fr.readAsDataURL(b), ng);
        });
        return `@font-face{font-family:"${f.family}";font-style:${f.style};font-weight:${f.weight};src:url(data:font/woff2;base64,${data}) format("woff2");unicode-range:${f.range};}`;
      } catch {
        return "";
      }
    }),
  );
  const out = css.filter(Boolean).join("\n");
  return out || null;
}

/** 開いていた画面の名前（ファイル名に使う）。1件の画面（:id）は題に顧客名などが入るので、どの帳票・どのメニューかだけ（いちばん奥の分かる区切り。/groups/:id/schedule は工程表） */
const SECTION: Record<string, string> = { home: "ホーム", groups: "案件", schedule: "工程表", estimates: "見積書", invoices: "請求書", receipts: "領収書", payments: "入金", partners: "顧客", settings: "設定", account: "アカウント", "vendor-quotes": "外注見積" };
export function screenLabel(path: string, pageName: string | null): string {
  const seg = path.split("/").filter(Boolean);
  const hasId = seg.some((x) => /^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(x) || /^[0-9]{3,}$/.test(x));
  const known = seg.filter((x) => x in SECTION);
  const section = SECTION[known[known.length - 1] ?? ""];
  const label = (!hasId && pageName) || section || "";
  return label.replace(/[\\/:*?"<>|\s]+/g, "").slice(0, 30);
}
/** 20261004_2041_見積書画面.png */
export function captureName(at: Date, label: string, type: "image/png" | "image/jpeg"): string {
  const p = (n: number) => String(n).padStart(2, "0");
  const stamp = `${at.getFullYear()}${p(at.getMonth() + 1)}${p(at.getDate())}_${p(at.getHours())}${p(at.getMinutes())}`;
  return `${stamp}_${label ? `${label}画面` : "画面"}.${type === "image/png" ? "png" : "jpg"}`;
}

/** 押した瞬間に呼ぶ（位置と塗りつぶす所はここで決める。そのあと窓が開いても写しは変わらない）。label＝開いている画面の名前（ファイル名） */
export function captureScreen(label = ""): Promise<ScreenShot | null> {
  const at = new Date();
  const root = document.getElementById("root");
  if (!root || typeof HTMLCanvasElement === "undefined") return Promise.resolve(null);
  const doc = document.documentElement;
  const w = doc.clientWidth;
  const h = doc.clientHeight;
  const sx = window.scrollX;
  const sy = window.scrollY;
  const masks: Rect[] = [];
  for (const el of Array.from(document.querySelectorAll(CAPTURE_MASK))) {
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < h && r.right > 0 && r.left < w) masks.push({ x: r.left, y: r.top, w: r.width, h: r.height });
  }
  // スマホの幅は 2倍で（文字が読めるように）。PC は等倍（大きくしない）
  const scale = w < 800 ? 2 : 1;
  const bg = getComputedStyle(document.body).backgroundColor || "#ffffff";
  const fetchFn = blobImages();
  // チェックの状態は押した瞬間に控える（写しの中では属性しか残らない）。終わったら外す
  const checks = Array.from(root.querySelectorAll<HTMLInputElement>('input[type="checkbox"],input[type="radio"]'));
  for (const c of checks) c.setAttribute(CHECKED, c.checked ? "1" : "0");
  const unmark = () => checks.forEach((c) => c.removeAttribute(CHECKED));
  const seen = new Map<Element, boolean>();
  const below = (el: Element) => {
    let v = seen.get(el);
    if (v === undefined) {
      v = el.getBoundingClientRect().top > h + 8;
      seen.set(el, v);
    }
    return v;
  };

  const work = (async (): Promise<ScreenShot | null> => {
    const [{ domToCanvas }, cssText] = await Promise.all([import("modern-screenshot"), fontCss(root).catch(() => null)]);
    const canvas = await domToCanvas(root, {
      width: w,
      height: h,
      scale,
      backgroundColor: bg,
      font: cssText ? { cssText } : false,
      timeout: 5000,
      // ページのスクロールの分だけ上へずらす（transform にしない＝position:fixed の要素が見えている位置のまま）
      style: { marginTop: `${-sy}px`, marginLeft: `${-sx}px` },
      features: { restoreScrollPosition: true },
      // 写しは #root から下だけ＝<html lang> が届かない。日本語の字（漢字の形）で描くよう写しの根に付け直す
      onCloneNode: (c) => {
        if (c instanceof Element) c.setAttribute("lang", document.documentElement.lang || "ja");
      },
      onCloneEachNode: (c) => {
        if (!(c instanceof Element)) return;
        if (c.matches(CAPTURE_MASK)) {
          // 塗りつぶす欄は中身ごと空に（位置がずれても文字が出ない）
          if (c instanceof HTMLInputElement) {
            c.setAttribute("value", "");
            c.setAttribute("type", "password");
          } else if (c instanceof HTMLTextAreaElement) {
            c.setAttribute("value", "");
            c.textContent = "";
          } else c.replaceChildren();
        }
        if (c instanceof HTMLSelectElement) {
          const v = c.getAttribute("value");
          for (const o of Array.from(c.querySelectorAll("option"))) o.toggleAttribute("selected", (o.getAttribute("value") ?? o.textContent) === v);
        }
        const k = c.getAttribute(CHECKED);
        if (k !== null) {
          c.toggleAttribute("checked", k === "1");
          c.removeAttribute(CHECKED);
        }
      },
      fetchFn,
      filter: (n) => {
        if (n instanceof Element && (n.matches(CAPTURE_SKIP) || n.id.startsWith("__SANDBOX__"))) return false;
        // 見えている範囲より下にある要素は、中身を描かない（箱は残す＝写した高さのまま並ぶ。箱ごと外すと、表の行が残りの高さに伸びて中身が消える）
        const p = n.parentElement;
        return !(p && p !== root && below(p));
      },
    });
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    const k = canvas.width / w;
    ctx.fillStyle = "#55534c";
    for (const m of masks) ctx.fillRect(Math.floor(m.x * k), Math.floor(m.y * k), Math.ceil(m.w * k), Math.ceil(m.h * k));
    let blob = await toBlob(canvas, "image/png");
    let type: ScreenShot["type"] = "image/png";
    if (!blob || blob.size > PNG_MAX) {
      blob = await toBlob(canvas, "image/jpeg", 0.85);
      type = "image/jpeg";
    }
    if (!blob || blob.size > SUPPORT_LIMITS.captureBytes) return null;
    return { blob, type, url: URL.createObjectURL(blob), width: canvas.width, height: canvas.height, name: captureName(at, label, type) };
  })();

  let late = false;
  const timeout = new Promise<null>((res) =>
    setTimeout(() => {
      late = true;
      res(null);
    }, TIMEOUT_MS),
  );
  // 時間切れのあとにできた写しは使わない（見本の URL も捨てる）。チェックの控えはどちらでも外す
  void work
    .then((s) => late && s && URL.revokeObjectURL(s.url))
    .catch(() => undefined)
    .finally(unmark);
  return Promise.race([work, timeout]).catch((e: unknown) => {
    console.warn("[support] 画面の写しを作れませんでした", e);
    return null;
  });
}
